import { clear, el } from '../utils/dom.js';
import { formatPace, formatTime } from '../ultra/ultraPacing.js';

const strategyClasses = {
  run: 'strategy-run',
  'run-hard': 'strategy-run-hard',
  hike: 'strategy-hike',
  critical: 'strategy-critical'
};

const strategyLabels = {
  run: 'RUN',
  'run-hard': 'RUN (HARD)',
  hike: 'POWER HIKE',
  critical: 'RUN (CRITICAL)'
};

function statItem(value, label, valueFontSizePx) {
  const valueEl = el('div', { class: 'stat-value', text: value });
  if (valueFontSizePx) valueEl.style.fontSize = `${valueFontSizePx}px`;

  return el('div', { class: 'stat-item' }, [
    valueEl,
    el('div', { class: 'stat-label', text: label })
  ]);
}

export function renderProfileDetails(detailsEl, state) {
  detailsEl.classList.add('gpx-profile-details');
  clear(detailsEl);

  const selectedSection = state.selectedSection;
  const data = state.fullAnalysisData;
  if (!selectedSection || !data) {
    detailsEl.appendChild(el('p', { class: 'copy-note', text: 'Select a section on the chart to view details.' }));
    return;
  }

  const { sections, totalDistance, ultraPacing, totalGAPDistance } = data;
  const sectionIndex = sections.findIndex(s => s.sectionNumber === selectedSection.sectionNumber);

  let cumulativeGain = 0;
  let cumulativeLoss = 0;
  let remainingGain = 0;
  let remainingLoss = 0;
  let cumulativeGAPDistance = 0;

  sections.forEach((section, index) => {
    if (index <= sectionIndex) {
      cumulativeGain += section.totalElevationGain;
      cumulativeLoss += section.totalElevationLoss;
      cumulativeGAPDistance += section.gapDistance || 0;
    } else {
      remainingGain += section.totalElevationGain;
      remainingLoss += section.totalElevationLoss;
    }
  });

  const distanceFromStartKm = selectedSection.endDistance / 1000;
  const remainingDistanceKm = (totalDistance - selectedSection.endDistance) / 1000;
  const remainingGAPDistanceKm = (totalGAPDistance - cumulativeGAPDistance) / 1000;
  const gapPercentCompleted = totalGAPDistance > 0 ? (cumulativeGAPDistance / totalGAPDistance) * 100 : 0;

  detailsEl.appendChild(el('div', { class: 'table-title', text: `Section ${selectedSection.sectionNumber} Details` }));

  const summaryGrid = el('div', { class: 'summary-stats' });
  summaryGrid.appendChild(
    el('div', { class: 'stat-item' }, [
      el('div', { class: 'stat-value' }, [
        el('span', { class: `section-type ${selectedSection.class}`, text: selectedSection.type })
      ]),
      el('div', { class: 'stat-label', text: 'Type' })
    ])
  );
  summaryGrid.appendChild(statItem((selectedSection.totalDistance / 1000).toFixed(2), 'Distance (km)', 20));
  summaryGrid.appendChild(statItem(`${selectedSection.averageGradient.toFixed(1)}%`, 'Avg. Gradient', 20));
  summaryGrid.appendChild(statItem(String(Math.round(selectedSection.totalElevationGain)), 'Gain (m)', 20));
  summaryGrid.appendChild(statItem(String(Math.round(selectedSection.totalElevationLoss)), 'Loss (m)', 20));
  summaryGrid.appendChild(statItem(String(selectedSection.segmentCount), 'Segments', 20));
  detailsEl.appendChild(summaryGrid);

  detailsEl.appendChild(el('h4', { class: 'gpx-subheading', text: 'Position Statistics' }));
  const posGrid = el('div', { class: 'summary-stats' });
  posGrid.appendChild(statItem(distanceFromStartKm.toFixed(2), 'Distance from Start (km)', 20));
  posGrid.appendChild(statItem(remainingDistanceKm.toFixed(2), 'Remaining Distance (km)', 20));
  posGrid.appendChild(statItem((cumulativeGAPDistance / 1000).toFixed(2), 'GAP Distance from Start (km)', 20));
  posGrid.appendChild(statItem(remainingGAPDistanceKm.toFixed(2), 'Remaining GAP Distance (km)', 20));
  posGrid.appendChild(statItem(`${gapPercentCompleted.toFixed(1)}%`, 'GAP % Completed', 20));
  posGrid.appendChild(statItem(String(Math.round(cumulativeGain)), 'Total Gain from Start (m)', 20));
  posGrid.appendChild(statItem(String(Math.round(remainingGain)), 'Remaining Gain (m)', 20));
  posGrid.appendChild(statItem(String(Math.round(cumulativeLoss)), 'Total Loss from Start (m)', 20));
  posGrid.appendChild(statItem(String(Math.round(remainingLoss)), 'Remaining Loss (m)', 20));
  detailsEl.appendChild(posGrid);

  if (ultraPacing) {
    const sectionPacing = ultraPacing.sectionPacing.find(sp => sp.sectionNumber === selectedSection.sectionNumber);
    if (sectionPacing) {
      const block = el('div', { class: 'gpx-ultra-details' });
      const heading = el('h4', { text: 'Ultra Pacing Strategy' });
      const badge = el('span', {
        class: `strategy-badge ${strategyClasses[sectionPacing.strategy] || ''}`,
        text: strategyLabels[sectionPacing.strategy] || sectionPacing.strategy
      });
      heading.appendChild(badge);
      block.appendChild(heading);

      const upGrid = el('div', { class: 'summary-stats' });
      upGrid.appendChild(statItem(`${formatPace(sectionPacing.pace)}/km`, 'Target Pace', 20));
      upGrid.appendChild(statItem(formatTime(sectionPacing.time), 'Section Time', 20));
      upGrid.appendChild(statItem(formatTime(sectionPacing.cumulativeTime), 'Cumulative Time', 20));
      upGrid.appendChild(statItem(`${Math.round(sectionPacing.fatiguePercent)}%`, 'Fatigue Level', 20));

      const sectionTimeHours = sectionPacing.time / 60;
      if (sectionTimeHours > 0) {
        if (selectedSection.averageGradient > 2) {
          const ascentRate = selectedSection.totalElevationGain / sectionTimeHours;
          upGrid.appendChild(statItem(String(Math.round(ascentRate)), 'Ascent Rate (m/h)', 20));
        } else if (selectedSection.averageGradient < -2) {
          const descentRate = selectedSection.totalElevationLoss / sectionTimeHours;
          upGrid.appendChild(statItem(String(Math.round(descentRate)), 'Descent Rate (m/h)', 20));
        }
      }

      block.appendChild(upGrid);

      if (sectionPacing.alert?.message) {
        block.appendChild(el('div', { class: 'pacing-alert' }, [
          el('strong', { text: 'Pacing Alert: ' }),
          sectionPacing.alert.message
        ]));
      }

      detailsEl.appendChild(block);
    }
  }
}

